import { updateClip, updateTranslatedCaption } from '@goldcast/player-config/src/retrievers/updateClip';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { prepareClipForFinalSave } from '@goldcast/player-config/src/utils/prepareClipForFinalSave';
import { areCaptionsObsolete, markCaptionsUpdated } from '../captions';
import { currentClip, updateClipInList, updateClipWithId } from '../clip';
import { addPendingUpdate } from '../pendingEditsStore';
import createStore from '../store';
import { defaultSharedAPIStore } from './constants';
import { computeCaptionsForClip } from '@/App/remotion/libs/caption/caption';
import { SpeakerSegmentContextType } from '@/context/SpeakerSegmentContext/SpeakerSegmentContextTypes';
import { TranscriptContextData } from '@/context/TranscriptContext/TranscriptContextTypes';

export interface SharedAPIStore {
  transcriptStore: TranscriptContextData;
  speakerSegmentStore: SpeakerSegmentContextType;
  mainPlayerRef: HTMLVideoElement | null;
  introPlayer: HTMLVideoElement | null;
  outroPlayer: HTMLVideoElement | null;
  speakerMap: Record<
    string,
    {
      videoRef: HTMLVideoElement;
    }
  >;
  speakerImageMap: Record<
    string,
    {
      imageRef: HTMLImageElement;
    }
  >;
}

export const sharedAPI = createStore<Record<string, SharedAPIStore>>({});

export function initSharedAPI(clipId: string) {
  sharedAPI.update(data => ({ ...data, [clipId]: defaultSharedAPIStore }));
}

export function updateSharedAPIWithId(clipId: string, data: Partial<SharedAPIStore>) {
  sharedAPI.update(store => ({ ...store, [clipId]: { ...store[clipId], ...data } }));
}

export function updateSpeakerImageMap(clipId: string, speakerKey: string, imageRef: HTMLImageElement) {
  sharedAPI.update(data => ({
    ...data,
    [clipId]: {
      ...data[clipId],
      speakerImageMap: {
        ...data[clipId].speakerImageMap,
        [speakerKey]: {
          ...data[clipId].speakerImageMap[speakerKey],
          imageRef
        }
      }
    }
  }));
}

export function updateSpeakerVideoMap(clipId: string, speakerKey: string, videoRef: HTMLVideoElement) {
  sharedAPI.update(data => ({
    ...data,
    [clipId]: {
      ...data[clipId],
      speakerMap: {
        ...data[clipId].speakerMap,
        [speakerKey]: {
          ...data[clipId].speakerMap[speakerKey],
          videoRef
        }
      }
    }
  }));
}

export function updateClipConfig({
  clipId,
  clip,
  isTranslatedCaptionEdited,
  skipRemotionUpdate
}: {
  clipId: string;
  clip?: Clip;
  isTranslatedCaptionEdited?: boolean;
  skipRemotionUpdate?: boolean;
}) {
  if (!clip) {
    clip = currentClip.getSnapshot()[clipId];
  }
  // eslint-disable-next-line prefer-const
  let { finalClip, version } = prepareClipForFinalSave(clip);

  const updateCaptionsWithFastEdit = areCaptionsObsolete(clipId);

  if (updateCaptionsWithFastEdit) {
    const { captions, excludes, duration } = computeCaptionsForClip(finalClip);

    finalClip = {
      ...finalClip,
      asset_metadata: {
        ...finalClip.asset_metadata,
        captions,
        excludes,
        duration
      }
    };

    markCaptionsUpdated({ clipId });
  }

  updateClipWithId(clipId, finalClip, true, skipRemotionUpdate);
  updateClipInList(finalClip);

  const updatePromise: Promise<any> =
    isTranslatedCaptionEdited && !updateCaptionsWithFastEdit
      ? updateTranslatedCaption(currentClip.getSnapshot()[clipId])
      : updateClip(currentClip.getSnapshot()[clipId]);

  addPendingUpdate({ clipId: finalClip.id, updatePromise, version });

  return updatePromise;
}
